import { Box, Typography, LinkButton } from "@bca/ui-library";
import { color, ColorProps, layout, LayoutProps } from "styled-system";
import css from "@styled-system/css";
import styled from "styled-components";

import TachometerFastSVG from "./Icons/TachometerFastSVG";
import BookSVG from "./Icons/BookSVG";
import WrenchSVG from "./Icons/WrenchSVG";
import { Vehicle } from "./Interfaces/vehicle.type";
import { formatMileage } from "./formatMileage";
import reportDataPush from "./AdobeDataPushes/vehicleCard/reportDataPush";
import { logbookMapper } from "./Mappers/vehicleDetailsMappers";

export const KeyDecisionInfoContainer = styled(Box)<LayoutProps>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  height: 100%;
  ${layout}
  ${css({
    ml: 5,
  })};
`;

interface IconProps extends ColorProps {}

const ConditionReportIcon = styled.div<IconProps>`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  ${css({
    fontSize: "body",
    mb: 1,
    fontWeight: "semiBold",
    color: "white",
  })};
  ${color};
`;

const gradeIcon = {
  "1": {
    color: "#009d45",
    icon: "1",
    label: "grade 1",
  },
  "2": {
    color: "#00c546",
    icon: "2",
    label: "grade 2",
  },
  "3": {
    color: "#a7dc26",
    icon: "3",
    label: "grade 3",
  },
  "4": {
    color: "#fcc800",
    icon: "4",
    label: "grade 4",
  },
  "5": {
    color: "#f56600",
    icon: "5",
    label: "grade 5",
  },
  "Retail ready": {
    color: "primary-navy-blue",
    icon: "R",
    label: "Retail Ready",
  },
  Unclassified: {
    color: "lightest-grey",
    icon: "U",
    label: "Unclassified",
  },
};

const mechanicalReportTitle = {
  Standard: "BCA Assured",
  ElectricHybrid: "BCA Assured EV/Hybrid",
  EssentialChecks: "BCA Essential Check",
  FullInspection: "128 Vehicle Inspection",
  NoReport: null,
};

const mechanicalReportLink = {
  Standard: "Assured report",
  ElectricHybrid: "EV/Hybrid report",
  EssentialChecks: "Essential Check report",
  FullInspection: "128 Vehicle Inspection report",
  NoReport: null,
};

const mileageWarranted = {
  true: "Warranted",
  false: "Not Warranted",
  null: "Unknown",
};

const KeyDecisionInfo: React.FunctionComponent<{
  vehicle: Vehicle;
  mediaServerServiceDegraded?: boolean;
}> = ({ vehicle, mediaServerServiceDegraded = false }) => {
  const conditionReportAnalytics = () =>
    reportDataPush(vehicle, "condition-report");
  const mechanicalReportAnalytics = () =>
    reportDataPush(vehicle, "assured-report");
  const logbookDisplayed = logbookMapper[vehicle.logBookPresent?.toString()];

  return (
    <Box display="flex" flexDirection="column" justifyItems="space-between">
      <Box display="flex">
        {vehicle.mileage && (
          <KeyDecisionInfoContainer width="50%" mb={4}>
            <Box display="flex" flexDirection="column">
              <Box display="flex" flexDirection="row">
                <TachometerFastSVG
                  width="19px"
                  height="14px"
                  mb={2}
                ></TachometerFastSVG>
                <Typography
                  variant="h6"
                  htmlTag="p"
                  color="footer-blue"
                  m={0}
                  ml={3}
                >
                  {`${formatMileage(vehicle.mileage)}`}
                </Typography>
              </Box>
              <Typography variant="body-small" color="grey-blue" m={0}>
                {mileageWarranted[String(vehicle.mileageWarranted)]}
              </Typography>
            </Box>
          </KeyDecisionInfoContainer>
        )}
        {vehicle.logBookPresent !== null && (
          <KeyDecisionInfoContainer width="50%">
            <Box display="flex" flexDirection="column">
              <Box display="flex" flexDirection="row">
                <BookSVG width="16px" height="18px" mb={1}></BookSVG>
                <Typography
                  variant="h6"
                  htmlTag="p"
                  color="footer-blue"
                  m={0}
                  ml={3}
                >
                  V5
                </Typography>
              </Box>
              <Typography variant="body-small" color="grey-blue" m={0}>
                {logbookDisplayed}
              </Typography>
            </Box>
          </KeyDecisionInfoContainer>
        )}
      </Box>
      <Box display="flex">
        {vehicle.grade &&
          vehicle.grade !== "Unknown" &&
          vehicle.conditionReport &&
          vehicle.conditionReport.type !== "Unknown" &&
          mediaServerServiceDegraded === false && (
            <KeyDecisionInfoContainer width="50%">
              <Box display="flex" flexDirection="column">
                <Box display="flex" flexDirection="row">
                  <ConditionReportIcon
                    backgroundColor={gradeIcon[vehicle.grade]?.color}
                  >
                    {gradeIcon[vehicle.grade]?.icon}
                  </ConditionReportIcon>
                  <Typography
                    variant="h6"
                    htmlTag="p"
                    color="footer-blue"
                    m={0}
                    ml={3}
                    css="text-transform: capitalize;"
                  >
                    {gradeIcon[vehicle.grade]?.label}
                  </Typography>
                </Box>
                <LinkButton
                  href={vehicle.conditionReport?.href}
                  onClick={conditionReportAnalytics}
                  variant="tertiary"
                  target="_blank"
                  color="primary-light-blue"
                  fontWeight="normal"
                  padding={0}
                  height="fit-content"
                  css="text-decoration: underline;"
                  fontSize="body-small"
                >
                  Condition Report
                </LinkButton>
              </Box>
            </KeyDecisionInfoContainer>
          )}
        {vehicle.mechanicalReport && mediaServerServiceDegraded === false && (
          <KeyDecisionInfoContainer width="50%">
            <Box display="flex" flexDirection="column">
              <Box display="flex" flexDirection="row">
                <WrenchSVG
                  width="18px"
                  minWidth="18px"
                  height="18px"
                  mb={1}
                ></WrenchSVG>
                <Typography
                  variant="h6"
                  htmlTag="p"
                  color="footer-blue"
                  m={0}
                  ml={3}
                >
                  {mechanicalReportTitle[vehicle.mechanicalReport?.type]}
                </Typography>
              </Box>
              <LinkButton
                href={vehicle.mechanicalReport?.href}
                onClick={mechanicalReportAnalytics}
                variant="tertiary"
                target="_blank"
                color="primary-light-blue"
                fontWeight="normal"
                padding={0}
                height="fit-content"
                css="text-decoration: underline;"
                fontSize="body-small"
              >
                {mechanicalReportLink[vehicle.mechanicalReport?.type]}
              </LinkButton>
            </Box>
          </KeyDecisionInfoContainer>
        )}
      </Box>
    </Box>
  );
};

export default KeyDecisionInfo;
